import React, { useCallback, useEffect, useState } from 'react';
import { searchShelfItems } from '../../services/shelfIndexAPI';
import { useHybridShelfIndexSync } from '../../hooks/useHybridShelfIndexSync';
import type { ShelfItem } from '../../types/apiTypes';
import { formatType } from '../../utils/shelfIndexUtils';
import { LabelCheckOutModal } from './StorageInfoModal';
import ProductInfoModal from './ProductInfoModal';
import '../labels/PrintBoxLabel.css';
import '../warehouse/SearchWarehouse.css';

interface ShelfContentsModalProps {
  readonly shelf: string;
  readonly onClose: () => void;
}

function areShelfItemRowsEqual(a: ShelfItem[] = [], b: ShelfItem[] = []): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i];
    const right = b[i];
    if (
      left.id !== right.id ||
      left.sku !== right.sku ||
      left.type !== right.type ||
      left.shelf_index !== right.shelf_index
    ) {
      return false;
    }
  }
  return true;
}

function ShelfContentsModal({ shelf, onClose }: ShelfContentsModalProps): React.ReactElement {
  const [items, setItems] = useState<ShelfItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [productInfoItem, setProductInfoItem] = useState<{ sku: string } | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<{
    loc: { id: string; shelf_index: string; type: string };
    sku: string;
  } | null>(null);

  const refreshItems = useCallback(async (source = 'unknown'): Promise<void> => {
    const showLoading = !source.startsWith('hybrid-');
    if (showLoading) setLoading(true);
    try {
      const result = await searchShelfItems(shelf);
      const nextItems = result.items || [];
      setItems((prev) => (areShelfItemRowsEqual(prev, nextItems) ? prev : nextItems));
    } catch {
      setItems([]);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [shelf]);

  useEffect(() => {
    void refreshItems('modal-open');
  }, [refreshItems]);

  useHybridShelfIndexSync({
    onSync: (source) => refreshItems(`hybrid-${source}`),
  });

  return (
    <div className="pbl-modal-overlay" onClick={onClose} role="presentation">
      <div className="pbl-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="pbl-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <div className="pbl-modal-title">Shelf Contents</div>
        {loading ? (
          <div className="sw-results-empty">Loading...</div>
        ) : items.length === 0 ? (
          <div className="sw-results-empty">No items found</div>
        ) : (
          <div className="sw-table-scroll">
            <table className="sw-results-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>SKU</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={`${item.sku}-${item.id}-${idx}`}>
                    <td className="sw-results-id-cell">
                      <button
                        type="button"
                        className="sw-results-id-clickable"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLabel({
                            loc: { id: item.id, shelf_index: shelf, type: item.type },
                            sku: item.sku,
                          });
                        }}
                      >
                        {item.id}
                      </button>
                    </td>
                    <td
                      className="sw-sku-clickable"
                      onClick={() => setProductInfoItem({ sku: item.sku })}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setProductInfoItem({ sku: item.sku });
                        }
                      }}
                    >
                      {item.sku}
                    </td>
                    <td className="sw-results-type-cell">{formatType(item.type)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {productInfoItem ? (
        <ProductInfoModal item={productInfoItem} onClose={() => setProductInfoItem(null)} />
      ) : null}
      {selectedLabel ? (
        <LabelCheckOutModal
          loc={selectedLabel.loc}
          sku={selectedLabel.sku}
          onClose={() => setSelectedLabel(null)}
          onSuccess={() => { void refreshItems(); }}
          checkedIn={false}
        />
      ) : null}
    </div>
  );
}

export default ShelfContentsModal;
